import { ArrowUpDown, MoreHorizontal } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { DataTable } from '@components/dataTable/DataTable';
import {
  RouteParams,
  RoutingViews,
  getRouteFromPath,
} from '@core/services/routingService';
import { Aircraft } from '@models/aircraft.model';
import MotionButton from '@motion/Button';
import MotionCheckbox from '@motion/Checkbox';
import MotionDropdown from '@motion/Dropdown';
import { Badge } from '@shadCN/ui/badge';
import { Button } from '@shadCN/ui/button';
import { Checkbox } from '@shadCN/ui/checkbox';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@shadCN/ui/dropdown-menu';
import { ColumnDef } from '@tanstack/react-table';

interface Props {
  aircraft: Aircraft[];
}

const AircraftTable: React.FC<Props> = ({ aircraft }) => {
  const CSSBlock = 'clients-table';
  const navigate = useNavigate();

  const columns: ColumnDef<Aircraft>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <MotionCheckbox
          isChecked={
            table.getIsAllPageRowsSelected() ||
            !!(table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          setIsChecked={value => table.toggleAllPageRowsSelected(!!value)}
        />
      ),
      cell: ({ row }) => (
        <MotionCheckbox
          isChecked={row.getIsSelected()}
          setIsChecked={value => row.toggleSelected(!!value)}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'manufacturer',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() =>
              column.toggleSorting(column.getIsSorted() === 'asc')
            }>
            Manufacturer
            <ArrowUpDown />
          </Button>
        );
      },
      enableHiding: false,
      cell: ({ row }) => (
        <div className="px-3">{row.getValue('manufacturer')}</div>
      ),
    },
    {
      accessorKey: 'model',
      enableHiding: false,
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() =>
              column.toggleSorting(column.getIsSorted() === 'asc')
            }>
            Model
            <ArrowUpDown />
          </Button>
        );
      },
      cell: ({ row }) => <div className="px-3">{row.getValue('model')}</div>,
    },
    {
      accessorKey: 'serialNumber',
      header: ({ column }) => <div className="px-3">Serial Number</div>,
      cell: ({ row }) => (
        <div className="px-3">{row.getValue('serialNumber')}</div>
      ),
    },
    {
      accessorKey: 'year',
      header: ({ column }) => <div className="px-3">Year</div>,
      cell: ({ row }) => <div className="px-3">{row.getValue('year')}</div>,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <div className="px-3">Status</div>,
      cell: ({ row }) => (
        <Badge variant="outline" className="mx-3 uppercase font-bold">
          {row.getValue('status')}
        </Badge>
      ),
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        return (
          <MotionDropdown
            triggerButtonProps={{ variant: 'ghost' }}
            triggerButtonContent={<MoreHorizontal />}
            dropdownContent={[
              <DropdownMenuItem
                onClick={() => {
                  navigate(
                    getRouteFromPath(RoutingViews.INVENTORY_AIRCRAFT, {
                      [RouteParams.AIRCRAFT_ID]: row.original.documentID,
                    }),
                    {
                      replace: true,
                    }
                  );
                }}>
                View aircraft details
              </DropdownMenuItem>,
              <DropdownMenuItem>View aircraft owner</DropdownMenuItem>,
              <DropdownMenuSeparator />,
              // <DropdownMenuLabel>Actions</DropdownMenuLabel>,
              <DropdownMenuItem
                onClick={() =>
                  navigator.clipboard.writeText(row.getValue('documentID'))
                }>
                Copy Aircraft ID
              </DropdownMenuItem>,
              <DropdownMenuItem onClick={() => {}}>
                Export Aircraft
              </DropdownMenuItem>,
            ]}
          />
        );
      },
    },
  ];
  return (
    <div className={`${CSSBlock}`}>
      <DataTable<Aircraft>
        columns={columns}
        data={aircraft}
        searchColumns={['manufacturer', 'model']}
        searchPlaceholder="Filter aircraft by name"
        toolbarButtons={[
          <MotionButton key="export-aircraft" disabled>
            Export Aircraft
          </MotionButton>,
          <MotionButton key="add-aircraft" disabled>
            Add Aircraft
          </MotionButton>,
        ]}
      />
    </div>
  );
};

export default AircraftTable;
